import { FC, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  UserPlus,
  Sparkles,
  TrendingUp,
  Eye,
  Target,
  Mail,
  MessageSquare,
  MailOpen,
  Link,
  FileText,
  UserMinus,
  User,
  Bell,
  Clipboard,
  LucideIcon,
  Loader2
} from "lucide-react"
import { Activity, LoanOfficer } from "../../../../types"
import { convertActivityToString } from "../../../utils"
import { useToast } from "../../../hooks"
import RoundedImg from "../../rounded-img"
import { useLazyGetThreadQuery } from "../../../../redux/queries"

interface Props {
  activity: Activity
  member?: LoanOfficer
  isLoActivity?: boolean
  isBrandActivity?: boolean
}

const getActivityRoute = (activity: Activity): string | null => {
  const { category, item_id, type } = activity

  if (!item_id) return null

  // SMS follow-up requires special handling with API call
  if (type === "sms_followup_sent") {
    return "async" // Special marker for async navigation
  }

  switch (category) {
    case "subscription":
      return `/engagements/${item_id}`
    case "lead":
      return `/leads/${item_id}`
    default:
      return null
  }
}

const getActivityTypeColor = (type: string, category: string): string => {
  switch (type) {
    case "new_lead":
      return "from-emerald-500 to-teal-500"
    case "new_subscription":
      return "from-violet-500 to-purple-500"
    case "dashboard_update":
    case "dashboard_pageview":
      return "from-blue-500 to-cyan-500"
    case "dashboard_first_visit":
    case "dashboard_initial_visit":
      return "from-indigo-500 to-blue-500"
    case "intro_email_sent":
    case "sms_followup_sent":
      return "from-amber-500 to-orange-500"
    case "intro_email_opened":
    case "intro_email_clicked":
      return "from-green-500 to-emerald-500"
    case "loan_summary_pdf":
    case "loan_summary_jpeg":
      return "from-slate-500 to-gray-500"
    case "unsubscribed":
      return "from-red-500 to-rose-500"
    default:
      return category === "lead"
        ? "from-teal-500 to-cyan-500"
        : category === "subscription"
        ? "from-purple-500 to-pink-500"
        : "from-slate-500 to-gray-500"
  }
}

const getActivityIcon = (type: string, category: string): LucideIcon => {
  switch (type) {
    case "new_lead":
      return UserPlus
    case "new_subscription":
      return Sparkles
    case "dashboard_update":
      return TrendingUp
    case "dashboard_pageview":
      return Eye
    case "dashboard_first_visit":
    case "dashboard_initial_visit":
      return Target
    case "intro_email_sent":
      return Mail
    case "sms_followup_sent":
      return MessageSquare
    case "intro_email_opened":
      return MailOpen
    case "intro_email_clicked":
      return Link
    case "loan_summary_pdf":
    case "loan_summary_jpeg":
      return FileText
    case "unsubscribed":
      return UserMinus
    default:
      return category === "lead"
        ? User
        : category === "subscription"
        ? Bell
        : Clipboard
  }
}

const ActivityCard: FC<Props> = ({
  activity,
  member,
  isLoActivity,
  isBrandActivity
}) => {
  const navigate = useNavigate()
  const { pushErrorToast } = useToast()
  const [isNavigating, setIsNavigating] = useState(false)
  const [getThread] = useLazyGetThreadQuery()

  const imageSrc = isBrandActivity
    ? member?.picture ?? member?.logo
    : member?.logo

  const showImage = imageSrc && (isLoActivity ? member?.picture : true)
  const ActivityIconComponent = getActivityIcon(
    activity.type,
    activity.category
  )
  const colorGradient = getActivityTypeColor(activity.type, activity.category)
  const route = getActivityRoute(activity)
  const isClickable = !!route

  const handleClick = async () => {
    if (!route) return

    // Handle SMS follow-up with async API call
    if (route === "async" && activity.type === "sms_followup_sent") {
      try {
        setIsNavigating(true)
        const params: { subscription_id?: string; lead_id?: string } = {}

        if (activity.category === "subscription") {
          params.subscription_id = String(activity.item_id)
        } else if (activity.category === "lead") {
          params.lead_id = String(activity.item_id)
        }

        const result = await getThread(params).unwrap()

        if (result && result._id) {
          // Navigate directly - ConversationsThread will handle loading by _id
          navigate(`/conversations/${result._id}`)
        } else {
          pushErrorToast("Conversation not found")
        }
      } catch (error) {
        console.error("Error fetching thread:", error)
        pushErrorToast("Unable to load conversation. Please try again.")
      } finally {
        setIsNavigating(false)
      }
    } else {
      // Direct navigation for other routes
      navigate(route)
    }
  }

  return (
    <div
      onClick={handleClick}
      className={`group relative flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2.5 py-2 sm:py-1.5 px-3 sm:px-2.5 transition-all duration-150 border-b border-slate-100/50 ${
        isClickable ? "cursor-pointer hover:bg-slate-50" : "cursor-default"
      } ${isNavigating ? "opacity-50 pointer-events-none" : ""}`}
    >
      {/* Icon/Avatar - responsive sizing */}
      <div className="relative z-10 flex-shrink-0">
        {isNavigating ? (
          <div className="w-8 sm:w-7 h-8 sm:h-7 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <Loader2 className="w-4 sm:w-3.5 h-4 sm:h-3.5 text-white animate-spin" />
          </div>
        ) : showImage ? (
          <div className="w-8 sm:w-7 h-8 sm:h-7 rounded-full overflow-hidden ring-1 ring-slate-200/50 group-hover:ring-primary-300 transition-all duration-150">
            {isLoActivity && member?.picture ? (
              <RoundedImg src={member.picture} alt={member?.name} />
            ) : (
              <img
                src={imageSrc}
                alt={member?.name}
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ) : (
          <div
            className={`w-8 sm:w-7 h-8 sm:h-7 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center`}
          >
            <ActivityIconComponent
              className="w-4 sm:w-3.5 h-4 sm:h-3.5 text-white"
              strokeWidth={2.5}
            />
          </div>
        )}
      </div>

      {/* Content - responsive text */}
      <div className="flex-1 min-w-0">
        <div className="text-[13px] sm:text-[12.5px] leading-tight text-slate-700 group-hover:text-slate-900 transition-colors duration-100">
          {convertActivityToString(activity)}
        </div>
      </div>
    </div>
  )
}

export default ActivityCard
